import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import SimpleTradeEditModal from './SimpleTradeEditModal';
import './ManageTrades.css';

function ManageTrades() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (productId) {
      loadProductAndTrades(productId);
    }
  }, [productId]);

  const fetchProducts = async () => {
    try {
      // Use EXACT same endpoint as Trade Tracker - Roger Scott's products
      const res = await api.get('/gurus/rogerscott/products');
      // Show same products as Trade Tracker - products with sheet_url
      const trackingProducts = res.data.filter(p => p.sheet_url);
      setProducts(trackingProducts);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load products:', err);
      setLoading(false);
    }
  };

  const loadProductAndTrades = async (prodId) => {
    try {
      setLoading(true);
      // Find product from already loaded products list
      const product = products.find(p => p.id === prodId);
      setSelectedProduct(product);

      const tradesRes = await api.get(`/admin/trades?product_id=${prodId}`);
      const tradesData = Array.isArray(tradesRes.data) ? tradesRes.data : tradesRes.data.trades || [];
      setTrades(tradesData);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load product trades:', err);
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    navigate(`/admin/trades/${product.id}`);
  };

  const handleBackToProducts = () => {
    setSelectedProduct(null);
    setTrades([]);
    navigate('/admin/trades');
  };

  const handleAddTrade = () => {
    setSelectedTrade(null);
    setIsAddMode(true);
    setEditModalOpen(true);
  };

  const handleEditTrade = (trade) => {
    setSelectedTrade(trade);
    setIsAddMode(false);
    setEditModalOpen(true);
  };

  const handleCloseTrade = async (trade) => {
    if (!window.confirm(`Close trade for ${trade.symbol}?`)) return;

    try {
      await api.patch(`/admin/trades/${trade.id}`, { status: 'closed' });
      loadProductAndTrades(selectedProduct.id);
    } catch (err) {
      console.error('Failed to close trade:', err);
      alert('Failed to close trade');
    }
  };

  const handleDeleteTrade = async (trade) => {
    // Double confirmation for delete
    const firstConfirm = window.confirm(
      `⚠️ DELETE TRADE?\n\n` +
      `Symbol: ${trade.symbol}\n` +
      `Entry Date: ${trade.entry_date}\n` +
      `Status: ${trade.status}\n\n` +
      `This action CANNOT be undone!\n\n` +
      `Are you absolutely sure you want to delete this trade?`
    );

    if (!firstConfirm) return;

    // Second confirmation
    const secondConfirm = window.confirm(
      `⚠️ FINAL CONFIRMATION\n\n` +
      `You are about to permanently delete the ${trade.symbol} trade.\n\n` +
      `Click OK to DELETE or Cancel to keep the trade.`
    );

    if (!secondConfirm) return;

    try {
      await api.delete(`/admin/trades/${trade.id}`);
      loadProductAndTrades(selectedProduct.id);
      alert('✅ Trade deleted successfully');
    } catch (err) {
      console.error('Failed to delete trade:', err);
      alert('❌ Failed to delete trade: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setSelectedTrade(null);
    setIsAddMode(false);
    if (selectedProduct) {
      loadProductAndTrades(selectedProduct.id);
    }
  };

  if (loading) {
    return <div className="manage-trades-loading">Loading...</div>;
  }

  // Show product list
  if (!selectedProduct) {
    return (
      <div className="manage-trades">
        <div className="manage-trades-header">
          <h1>Manage Trades</h1>
          <p>Select a product to view and manage trades</p>
        </div>

        <div className="admin-products-grid">
          {products.map(product => (
            <div
              key={product.id}
              className="product-card-admin"
              onClick={() => handleProductClick(product)}
            >
              {product.logo_url ? (
                <img src={product.logo_url} alt={product.name} className="product-logo-admin" />
              ) : (
                <div className="product-logo-placeholder-admin">
                  {product.name.substring(0, 2).toUpperCase()}
                </div>
              )}
              <h3>{product.name}</h3>
              <p className="product-sku">{product.sku}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Show trades for selected product
  return (
    <div className="manage-trades">
      <div className="manage-trades-header">
        <button onClick={handleBackToProducts} className="back-btn">
          ← Back to Products
        </button>
        <h1>{selectedProduct.name}</h1>
        <button onClick={handleAddTrade} className="add-trade-btn">
          + Add Trade
        </button>
      </div>

      <div className="trades-table-container">
        <table className="admin-trades-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Entry Date</th>
              <th>Exit Date</th>
              <th>Status</th>
              <th>Entry Price</th>
              <th>Exit Price</th>
              <th>P/L</th>
              <th>Return %</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {trades.map(trade => (
              <tr key={trade.id}>
                <td>{trade.symbol}</td>
                <td>{trade.entry_date}</td>
                <td>{trade.exit_date || '-'}</td>
                <td>
                  <span className={`status-badge-admin status-${trade.status}`}>
                    {trade.status}
                  </span>
                </td>
                <td>${trade.entry_price || trade.option_entry_price || '-'}</td>
                <td>${trade.exit_price || trade.option_exit_price || '-'}</td>
                <td className={parseFloat(trade.stock_profit_loss || trade.option_profit_loss || 0) >= 0 ? 'positive' : 'negative'}>
                  ${trade.stock_profit_loss || trade.option_profit_loss || '-'}
                </td>
                <td className={parseFloat(trade.stock_percent_return || trade.option_percent_return || 0) >= 0 ? 'positive' : 'negative'}>
                  {trade.stock_percent_return || trade.option_percent_return || '-'}%
                </td>
                <td className="actions-cell">
                  <button onClick={() => handleEditTrade(trade)} className="edit-btn-small">
                    Edit
                  </button>
                  {trade.status === 'open' && (
                    <button onClick={() => handleCloseTrade(trade)} className="close-btn-small">
                      Close
                    </button>
                  )}
                  <button onClick={() => handleDeleteTrade(trade)} className="delete-btn-small">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editModalOpen && (
        <SimpleTradeEditModal
          trade={selectedTrade}
          product={selectedProduct}
          isAddMode={isAddMode}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}

export default ManageTrades;
